import React, { useMemo } from 'react';
import { Button, message, Tag, Tooltip } from 'antd';
import { CopyOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import AssetAmount from '@/components/AssetView/AssetAmount';
import AssetSymbol from '@/components/AssetView/AssetSymbol';
import AddressItem from '@/components/AddressItem';
import { ITransaction } from '@/api/commitment';
import { SupportedChain } from '@packet/constants/AppSupportChains';
import { getTransactionAmount, getTransactionFromAddresses, getTransactionToAddresses, getTransactionTypeName, formatTransactionTime, getTransactionAssetId } from '@/utils/transaction';
import { TransactionType as TransactionTypeEnum } from '@/types/commitment';
import { Navigate } from 'react-router-dom';

// 交易状态枚举
export enum TransactionStatus {
  CONFIRM = 'confirm',
  PENDING = 'pending',
  FAILED = 'failed',
}

// 交易类型使用字符串类型，与系统中的TransactionTypeName保持一致
export type TransactionType = string;

// Overview组件的Props接口
export interface OverviewProps {
  transactionHash: string;
  chain: SupportedChain;
  transaction: ITransaction;
}

// 状态标签配置
const STATUS_CONFIG = {
  [TransactionStatus.CONFIRM]: {
    color: 'rgba(166, 235, 122, 0.2)',
    textColor: '#388506',
    icon: <CheckCircleOutlined />,
    text: 'Confirm',
  },
  [TransactionStatus.PENDING]: {
    color: '#f5f5f5',
    textColor: '#141413',
    icon: <ClockCircleOutlined />,
    text: 'Pending',
  },
  [TransactionStatus.FAILED]: {
    color: 'rgba(199, 35, 17, 0.1)',
    textColor: '#c72311',
    icon: <CloseCircleOutlined />,
    text: 'Failed',
  },
};

export default function Overview({ transactionHash, chain, transaction }: OverviewProps) {
  // 处理数据转换
  const fromAddresses = useMemo(() => getTransactionFromAddresses(transaction), [transaction]);

  const toAddresses = useMemo(() => {
    const list = getTransactionToAddresses(transaction);
    return { evm: list.filter((item) => item.type === 'evm'), app: list.filter((item) => item.type === 'app') };
  }, [transaction]);

  const amount = getTransactionAmount(transaction);
  const assetId = getTransactionAssetId(transaction);
  const feeSegments = useMemo(() => {
    const protocolFees = transaction.protocol_fee?.map((fee) => BigInt(fee)) ?? [];
    const relayerFee = transaction.relayer_fee ? BigInt(transaction.relayer_fee) : BigInt(0);

    if (!assetId) return [];

    const protocolFeeSum = protocolFees.reduce((acc, curr) => acc + curr, BigInt(0));
    return [{ assetId, amount: protocolFeeSum + relayerFee }];
  }, [assetId, transaction.inputs, transaction.outputs, transaction.protocol_fee, transaction.relayer_fee, transaction.tx_type]);
  const time = formatTransactionTime(transaction.created_at);
  const typeName = getTransactionTypeName(transaction.tx_type);
  const statusConfig = STATUS_CONFIG['confirm']; // 只有确认状态

  if (!assetId) {
    message.error('Transaction asset not found');
    return <Navigate to="/transactions" />;
  }

  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    message.success('Copied to clipboard');
  };

  return (
    <OverviewContainer>
      {/* 交易哈希 */}
      <HashSection>
        <HashText>{transactionHash}</HashText>
        <CopyButton onClick={() => handleCopy(transactionHash)}>
          <CopyOutlined />
        </CopyButton>
      </HashSection>

      {/* 概览信息 */}
      <OverviewSection>
        <SectionTitle>
          <TitleIndicator />
          <SectionTitleText>Overview</SectionTitleText>
        </SectionTitle>

        <InfoGrid>
          <InfoRow>
            <InfoLabel>State</InfoLabel>
            <InfoValue>
              <StatusTagsContainer>
                <StatusTag color={statusConfig.color} $textColor={statusConfig.textColor}>
                  {statusConfig.icon}
                  <span>{statusConfig.text}</span>
                </StatusTag>
                {/* 其他状态标签作为示例 */}
                {/* <StatusTag
                  color="#f5f5f5"
                  textColor="#141413"
                >
                  <ClockCircleOutlined />
                  <span>Pending</span>
                </StatusTag>
                <StatusTag
                  color="rgba(199, 35, 17, 0.1)"
                  textColor="#c72311"
                >
                  <CloseCircleOutlined />
                  <span>Failed</span>
                </StatusTag> */}
              </StatusTagsContainer>
            </InfoValue>
          </InfoRow>

          <InfoRow>
            <InfoLabel>Time</InfoLabel>
            <InfoValue>{time}</InfoValue>
          </InfoRow>

          <InfoRow>
            <InfoLabel>Network</InfoLabel>
            <InfoValue>{chain.name}</InfoValue>
          </InfoRow>

          <InfoRow>
            <InfoLabel>Type</InfoLabel>
            <InfoValue>{typeName}</InfoValue>
          </InfoRow>

          <InfoRow>
            <InfoLabel>From</InfoLabel>
            <InfoValue>
              {fromAddresses.map((item) => (
                <AddressItem chainId={chain.id} key={item.address} address={item.address} type={item.type} onCopy={handleCopy} />
              ))}
            </InfoValue>
          </InfoRow>

          <InfoRow>
            <InfoLabel>To</InfoLabel>
            <InfoValue>
              {toAddresses.app.map((item) => (
                <AddressItem chainId={chain.id} key={item.address} address={item.address} type={item.type} onCopy={handleCopy} />
              ))}
              {toAddresses.evm.map((item) => (
                <AddressItem chainId={chain.id} key={item.address} address={item.address} type={item.type} onCopy={handleCopy} />
              ))}
            </InfoValue>
          </InfoRow>

          <InfoRow>
            <InfoLabel>
              Amount
              <Tooltip title="Total transaction amount processed." overlayInnerStyle={{ width: '180px' }}>
                <QuestionCircleOutlined style={{ color: 'rgba(20, 20, 0, 0.6)', fontSize: '14px', marginLeft: 4 }} />
              </Tooltip>
            </InfoLabel>
            <InfoValue>
              <AssetAmount assetId={assetId} chainId={chain.id} amount={amount} /> <AssetSymbol assetId={assetId} chainId={chain.id} />
            </InfoValue>
          </InfoRow>

          {toAddresses.evm.length > 0 && transaction.amount && (
            <>
              <InfoRow>
                <InfoLabel>Withdraw Amount</InfoLabel>
                <InfoValue>
                  <AssetAmount assetId={assetId} chainId={chain.id} amount={transaction.amount} /> <AssetSymbol assetId={assetId} chainId={chain.id} />
                </InfoValue>
              </InfoRow>
            </>
          )}
          <InfoRow>
            <InfoLabel>Fee</InfoLabel>
            <InfoValue>
              {feeSegments.length > 0 ? (
                <FeeList>
                  {feeSegments.map((segment, index) => (
                    <FeeLine key={`${segment.assetId}-${index}`}>
                      <AssetAmount assetId={segment.assetId} chainId={chain.id} amount={segment.amount} /> <AssetSymbol assetId={segment.assetId} chainId={chain.id} />
                    </FeeLine>
                  ))}
                </FeeList>
              ) : (
                '--'
              )}
            </InfoValue>
          </InfoRow>
        </InfoGrid>
      </OverviewSection>
    </OverviewContainer>
  );
}

// Styled Components
const OverviewContainer = styled.div`
  width: 100%;
`;

const HashSection = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(243, 245, 242, 0.5);
  padding: 14px 20px;
  border-radius: 10px;
  margin-bottom: 45px;
  width: 100%;

  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
`;

const HashText = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #141413;
  flex: 1;
  line-height: 1;
  word-break: break-all;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const CopyButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  opacity: 0.6;
  color: #141413;
  font-size: 14px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.8;
  }
`;

const OverviewSection = styled.div`
  width: 100%;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
`;

const TitleIndicator = styled.div`
  width: 2px;
  height: 16px;
  background-color: #141413;
`;

const SectionTitleText = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #141413;
  margin: 0;
`;

const InfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  min-height: 56px;

  &:not(:last-child) {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    min-height: auto;
    padding: 16px 0;
    gap: 12px;
  }
`;

const InfoLabel = styled.div`
  width: 180px;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: rgba(20, 20, 0, 0.6);
  padding-right: 24px;
  flex-shrink: 0;
  line-height: 1;

  @media (max-width: 768px) {
    width: 100%;
    padding-right: 0;
    margin-bottom: 4px;
  }
`;

const InfoValue = styled.div`
  flex: 1;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #141413;
  padding-left: 32px;
  line-height: 1;

  @media (max-width: 768px) {
    padding-left: 0;
    width: 100%;
  }
`;

const StatusTagsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StatusTag = styled.div<{ color: string; $textColor: string }>`
  display: flex;
  align-items: center;
  gap: 3px;
  background-color: ${(props) => props.color};
  color: ${(props) => props.$textColor};
  padding: 4px 6px;
  border-radius: 4px;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1;

  svg {
    font-size: 16px;
  }
`;

const AddressContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FeeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FeeLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const AddressText = styled.span<{ color?: string }>`
  color: ${(props) => props.color};
  word-break: break-all;
  line-height: 1.4;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CopyIconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  opacity: 0.6;
  color: #141413;
  font-size: 16px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    opacity: 0.8;
  }
`;
